import { Stack } from "@mantine/core"

import { SavedViewsResponses } from "@costory/types/endpoints/savedViews"

import { FilterBar } from "@costory/front/components/FilterBar"
import { SavedViewsSelector } from "@costory/front/components/SavedViewsSelector"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  ChartContext,
  useChartContext,
} from "@costory/front/contexts/ChartContext"
import { useAxesQuery } from "@costory/front/queries/axes"
import { useBusinessMetricsQuery } from "@costory/front/queries/businessMetrics"
import { SavedViewRedirectPage } from "@costory/front/queries/savedViews"

type Children = React.ReactNode | (() => React.ReactNode)

type RootProps = {
  redirectPage: SavedViewRedirectPage
  savedView?: SavedViewsResponses.GetSavedViewDetails
  defaultHideOneTime?: boolean
  embed?: boolean
  children: Children
  showComparisonChart?: boolean
  toggleShowComparisonChart?: () => void
}

// The `key` is forced here because it's used to properly rerender form when saving a view
type QueryContainerProps = RootProps & { key: string }

type Props = {
  children: Children
  showComparisonChart?: boolean
  toggleShowComparisonChart?: () => void
}

const _ChartContainer = ({
  children,
  showComparisonChart,
  toggleShowComparisonChart,
}: Props) => {
  const {
    filterForm,
    metricsOptions,
    groupByOptions,
    referenceFilters,
    urlFilters,
    queryError,
    savedView,
    embed,
    redirectPage,
  } = useChartContext()

  return (
    <Stack gap={16}>
      {!embed && (
        <>
          <SavedViewsSelector
            currentView={savedView}
            redirectPage={redirectPage}
            form={filterForm}
            referenceFilters={referenceFilters}
            urlFilters={urlFilters}
          />
          <FilterBar
            form={filterForm}
            metricsOptions={metricsOptions}
            groupByOptions={groupByOptions}
            queryError={queryError}
            showComparisonChart={showComparisonChart}
            toggleShowComparisonChart={toggleShowComparisonChart}
          />
        </>
      )}
      {typeof children === "function" ? children() : children}
    </Stack>
  )
}

export const ChartContainer = ({
  savedView,
  redirectPage,
  embed = false,
  children,
  defaultHideOneTime,
  showComparisonChart,
  toggleShowComparisonChart,
}: QueryContainerProps) => {
  const axesQuery = useAxesQuery()
  const metricsQuery = useBusinessMetricsQuery()
  return (
    <QueryWrapper query={axesQuery} allowEmptyArray>
      {({ data: axes }) => (
        <QueryWrapper query={metricsQuery} allowEmptyArray>
          {({ data: metrics }) => (
            <ChartContext.Provider
              value={{
                axes,
                metrics,
                defaultHideOneTime,
                savedView,
                redirectPage,
                embed,
              }}
            >
              <_ChartContainer
                showComparisonChart={showComparisonChart}
                toggleShowComparisonChart={toggleShowComparisonChart}
              >
                {children}
              </_ChartContainer>
            </ChartContext.Provider>
          )}
        </QueryWrapper>
      )}
    </QueryWrapper>
  )
}
